import { useState } from "react";
import { useAccount, useSignTypedData } from "wagmi";
import { base } from "wagmi/chains";
import type { Address, Hex } from "viem";
import { publishRouteNote } from "../../lib/x402RouteNote";

/**
 * Publishing the endpoint description (a signed route note) is the step that
 * turns a hash-only recognition into a searchable listing. It is separate from
 * the routed Boon burn: no burn, no payment, just a wallet signature. Only a
 * recorded route participant (the tipper who recognized it) can publish it, so
 * this renders after recognition or on a route the connected wallet recognized.
 */
export function X402PublishDetails({
  endpointUrl,
  method,
  usdc,
  onPublished,
}: {
  endpointUrl: string;
  method: string;
  usdc: Address;
  onPublished?: () => void;
}) {
  const { address } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const [intent, setIntent] = useState("");
  const [fit, setFit] = useState("");
  const [status, setStatus] = useState<"idle" | "publishing" | "published">("idle");
  const [error, setError] = useState<string | null>(null);

  async function publish() {
    if (!address || !intent.trim() || !fit.trim()) return;
    setError(null);
    setStatus("publishing");
    try {
      await publishRouteNote({
        endpointUrl,
        method,
        intent: intent.trim(),
        fit: fit.trim(),
        publisher: address as Address,
        asset: usdc,
        chainId: base.id,
        signTypedData: (typedData) => signTypedDataAsync(typedData as never) as Promise<Hex>,
      });
      setStatus("published");
      onPublished?.();
    } catch (cause) {
      setStatus("idle");
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  if (status === "published") {
    return (
      <div className="mt-3 animate-fade-up flex items-start gap-3">
        <span className="animate-check-pop mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-olive text-paper">
          <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10.5l3.5 3.5L16 5.5" /></svg>
        </span>
        <p className="text-sm text-ink">Endpoint details published. It is now searchable by its URL.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="mt-3 text-sm text-muted leading-relaxed">
        One more step to make it findable: publish a short description. This is a wallet-signed <span className="text-ink">route note</span> (no burn, no payment) that records what the endpoint is for, so it surfaces when people search its URL. You can sign it because you recognized it.
      </p>
      <div className="mt-3 grid gap-3 max-w-xl">
        <label className="block text-xs text-muted btn-mono">
          What is this endpoint for?
          <input
            className="mt-1 w-full border border-faint rounded-md bg-paper px-3 py-2 text-ink"
            value={intent}
            maxLength={280}
            placeholder="e.g. Fresh knowledge-delta feed for agents"
            onChange={(e) => setIntent(e.target.value)}
          />
        </label>
        <label className="block text-xs text-muted btn-mono">
          Who or what does it fit?
          <input
            className="mt-1 w-full border border-faint rounded-md bg-paper px-3 py-2 text-ink"
            value={fit}
            maxLength={280}
            placeholder="e.g. Agents that need current deltas before acting"
            onChange={(e) => setFit(e.target.value)}
          />
        </label>
      </div>
      <button
        type="button"
        className="btn btn-primary mt-3 inline-flex items-center gap-2"
        onClick={publish}
        disabled={status === "publishing" || !address || !intent.trim() || !fit.trim()}
      >
        {status === "publishing" && (
          <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
          </svg>
        )}
        {status === "publishing" ? "Signing and publishing…" : "Publish endpoint details"}
      </button>
      {!address && <p className="mt-2 text-xs text-muted">Connect the wallet you recognized this endpoint with to publish.</p>}
      {error && <p className="mt-2 text-sm text-danger break-words">{error}</p>}
    </div>
  );
}
